import { Clock, NavStatistic } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import {
  IconWithBackground,
  ListCardItemWithIcon,
  paragraphSizes,
  ParagraphWithTooltip,
  SelectableButtonsGroup,
  SelectableButtonsGroupItemProps,
  ToggleActionCard,
} from '@va/ui/design-system';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useNotificationPreferences, { ReportTypeEnum } from './useNotificationPreferences';

type EmailReportsToggleProps = {
  size?: 'small' | 'medium' | 'large';
  className?: string;
};

export const EmailReportsToggle = ({ size = 'large', className }: EmailReportsToggleProps) => {
  const translate = useTranslate();
  const isContributor = useSelector(contributorRole);
  const { formValues, onFrequencyChange, getFrequency, selectedFrequency, onReportsChange } =
    useNotificationPreferences();

  const frequencyButtons = useMemo<SelectableButtonsGroupItemProps[]>(
    () =>
      getFrequency().map((button) => ({
        ...button,
        className: classNames({
          'h-16u! py-0 rounded-sm! text-interface-regular! leading-interface-regular!': size === 'small',
        }),
      })),
    [getFrequency, size],
  );

  return (
    <ToggleActionCard
      title={translate('form.emailReports.statisticsReports')}
      subtitle={translate('form.emailReports.reportsSubtitle')}
      name={'sendEmailReports'}
      subSectionClass={classNames({ 'pl-1u': size !== 'small' })}
      value={!isNil(formValues.reportType) && formValues.reportType !== ReportTypeEnum.never}
      handleChange={onReportsChange}
      disabled={isContributor}
      infoTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
      icon={() => (
        <IconWithBackground
          className={classNames({
            'w-10u! h-10u!': size === 'small',
          })}
          icon={() => <NavStatistic />}
        />
      )}
      wrapperClass={className}
      size={size}
    >
      <div className={'flex w-full justify-between rounded-12 sm:flex-col bg-white-snow flex-wrap space-y-2u'}>
        <ListCardItemWithIcon
          className='max-w-full!'
          icon={
            <IconWithBackground
              className={classNames({
                'w-10u! h-10u!': size === 'small',
              })}
              icon={() => <Clock strokeColor='#696969' />}
            />
          }
          topText={translate('unsubscribePage.frequencyReports')}
          topTextProps={{
            className: classNames({
              'text-interface-regular leading-interface-regular': size === 'small',
            }),
          }}
          bottomNode={
            <ParagraphWithTooltip
              size={paragraphSizes.tiny}
              colorClassName='text-primary'
              className={classNames('text-devil-gray!', {
                'leading-interface-small': size === 'small',
              })}
            >
              {translate('form.emailReports.frequencyReportsSubtitle')}
            </ParagraphWithTooltip>
          }
        />
        <div className='overflow-x-auto scrollbar scrollbar-thumb flex self-center mx-auto'>
          <SelectableButtonsGroup
            buttons={frequencyButtons}
            selectedValue={selectedFrequency}
            onChange={(value) => onFrequencyChange({ name: 'reportType', value: Number(value) })}
            className={classNames('bg-white-smoke! justify-center!', {
              'rounded-9px! gap-2u': size === 'small',
            })}
            flexDirectionClass={classNames({ 'gap-2u': size === 'small' })}
            activeButtonColor='secondary'
            inactiveButtonColor='tertiary'
            small
          />
        </div>
      </div>
    </ToggleActionCard>
  );
};
